import * as ngCdkOverlay from '@angular/cdk/overlay'
import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'

/**
 * A centered overlay component that can be used to display a dialog. It is
 * positioned at the center of the page by default, but its position can be
 * overridden using an input property.
 *
 * #### Inputs
 * - {@link Component.hasBackdrop|hasBackdrop}?
 * - {@link Component.backdropClass|backdropClass}?
 * - {@link Component.position|position}?
 *
 * #### Outputs
 * - {@link Component.backdropClick|backdropClick}
 *
 * @example
 * ```html
 * <!--
 *  The overlay will be displayed when the condition is true.
 *  It uses content projection to specify the content to display inside the overlay.
 * -->
 * <atn-ds-centered-overlay *ngIf="condition">
 *  <div>Overlay Content</div>
 * </atn-ds-centered-overlay>
 * ```
 */
@ngCore.Component({
  imports: [ngCdkOverlay.OverlayModule],
  selector: 'atn-ds-centered-overlay',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link ngCdkOverlay.Overlay} */
  private readonly overlay = ngCore.inject(ngCdkOverlay.Overlay)

  /**
   * The document to use when determining position.
   * it is inner to the component HTML, and should not be changed.
   */
  public readonly body = ngCore.inject(ngCommon.DOCUMENT)?.body

  /**
   * Whether the dialog has a backdrop.
   *
   * @default true
   */
  public readonly hasBackdropSg = ngCore.input<boolean>(true, {
    alias: 'hasBackdrop',
  })

  /**
   * The class to apply to the backdrop.
   *
   * @default 'ds-global-overlay-backdrop'
   */
  public readonly backdropClassSg = ngCore.input<string>(
    'ds-global-overlay-backdrop',
    { alias: 'backdropClass' },
  )

  /**
   * The position of the dialog. Check {@link ngCdkOverlay.GlobalPositionStrategy}
   *
   * @default overlay.position().global().centerHorizontally().centerVertically()
   */
  public readonly positionSg =
    ngCore.input<ngCdkOverlay.FlexibleConnectedPositionStrategy>(
      // FIXME: This casting is a workaround to avoid the type error. It appears
      // that global position strategy returns configuration that flexible
      // position strategy accepts and it centers overlay as it should. Find
      // better solution to convert global to flexible position strategy or use
      // only flexible position strategy.
      this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically() as unknown as ngCdkOverlay.FlexibleConnectedPositionStrategy,
      {
        alias: 'position',
      },
    )

  /**
   * Indicates the modal type, which determines the modal's size and appearance
   */
  public readonly typeSg = ngCore.input<
    'narrow' | 'wide' | 'full-width' | 'confirmation' | 'loader'
  >('narrow', { alias: 'type' })

  /** Returns styles based on the specified modal type */
  public readonly styleSg = ngCore.computed<Partial<CSSStyleDeclaration>>(
    (type = this.typeSg()) => {
      switch (type) {
        case 'narrow':
          return {
            height: '500px',
            width: '500px',
          }
        case 'wide':
          return {
            height: 'calc(100vh - 100px)',
            margin: '80px 0 20px 0',
            width: '700px',
          }
        case 'full-width': {
          // Sidenav width in px
          const navbarWidth = 70
          // Horizontal margins in px
          const margins = 40

          return {
            boxSizing: 'border-box',
            height: 'calc(100vh - 100px)',
            margin: '80px 20px 20px 90px',
            width: `calc(100vw - ${navbarWidth + margins}px)`,
          }
        }
        case 'confirmation':
          return {
            height: '255px',
            width: '460px',
          }
        case 'loader':
          return {
            height: '100vh',
            width: '100vw',
          }
      }
    },
  )

  /**
   * Event emitted when the backdrop is clicked.
   */
  @ngCore.Output()
  public backdropClick = new ngCore.EventEmitter<MouseEvent>()
}
